import type { Beat, Harmony, KitId, Lane, MusicEvent, Section, SectionName } from '../core/types';
import { createRng } from '../core/rng';
import { COUNT_IN_BARS, SONG_FORM, type FormPart } from '../core/chart';
import { BEATS_PER_BAR } from './plan';

export const SAFE_TOTAL_LEVEL = 0.95;
export const LANE_GAIN_BUDGET: Record<Lane, number> = {
  kick: 0.25,
  snare: 0.17,
  hat: 0.08,
  bass: 0.18,
  chord: 0.12,
  lead: 0.07,
  sub: 0.08,
};

export const CHORD_VOICE_LIMIT = 4;

const INTENSITY_FLOOR = 0.55;
const CHORD_SEVENTH_INTENSITY = 0.6;
const CHORD_OCTAVE = 2;
const BASS_OCTAVE = -1;
const LEAD_OCTAVE = 3;
const SEMITONES_PER_OCTAVE = 12;

const SECTION_BASE_INTENSITY: Record<SectionName, number> = {
  intro: 0.18,
  groove: 0.46,
  build: 0.66,
  peak: 0.92,
  break: 0.92,
  outro: 0.4,
};

const SECTION_DENSITY: Record<SectionName, number> = {
  intro: 0.5,
  groove: 0.85,
  build: 0.6,
  peak: 1,
  break: 0.3,
  outro: 0.6,
};

const SECTION_LANES: Record<SectionName, readonly Lane[]> = {
  intro: ['hat', 'bass', 'chord'],
  groove: ['kick', 'snare', 'hat', 'bass', 'chord'],
  build: ['kick', 'hat', 'bass', 'chord', 'lead'],
  peak: ['kick', 'snare', 'hat', 'bass', 'chord', 'lead'],
  break: ['bass', 'chord', 'lead'],
  outro: ['kick', 'hat', 'bass', 'chord'],
};

const MINOR_SCALE: readonly number[] = [0, 2, 3, 5, 7, 8, 10];

const PROGRESSIONS: readonly (readonly number[])[] = [
  [0, 5, 3, 4],
  [0, 3, 5, 4],
  [0, 4, 5, 3],
  [5, 3, 0, 4],
  [0, 6, 3, 4],
];

const KIT_ROOT_HZ: Record<KitId, number> = { edm: 110, phonk: 87.31, funk: 98 };

const LANE_ORDER: readonly Lane[] = ['kick', 'snare', 'sub', 'bass', 'chord', 'lead', 'hat'];

interface Step {
  readonly at: Beat;
  readonly dur: Beat;
  readonly accent: number;
  readonly minIntensity: number;
}

type DrumLane = 'kick' | 'snare' | 'hat';

const BEATS_ON: readonly number[] = [0, 1, 2, 3];
const BEATS_OFF: readonly number[] = [0.5, 1.5, 2.5, 3.5];
const SIXTEENTH_OFFS: readonly number[] = [0.25, 0.75, 1.25, 1.75, 2.25, 2.75, 3.25, 3.75];
const TRIPLET_EIGHTHS: readonly number[] = Array.from({ length: 12 }, (_, i) => i / 3);

function steps(offsets: readonly number[], dur: Beat, accent: number, minIntensity: number): Step[] {
  return offsets.map((at) => ({ at, dur, accent, minIntensity }));
}

const DRUM_PATTERNS: Record<KitId, Record<DrumLane, readonly Step[]>> = {
  edm: {
    kick: [
      { at: 0, dur: 0.5, accent: 1, minIntensity: 0 },
      { at: 1, dur: 0.5, accent: 0.85, minIntensity: 0.08 },
      { at: 2, dur: 0.5, accent: 0.95, minIntensity: 0.05 },
      { at: 3, dur: 0.5, accent: 0.85, minIntensity: 0.08 },
    ],
    snare: [
      { at: 1, dur: 0.5, accent: 0.9, minIntensity: 0.1 },
      { at: 3, dur: 0.5, accent: 0.95, minIntensity: 0.1 },
      { at: 3.75, dur: 0.25, accent: 0.45, minIntensity: 0.6 },
    ],
    hat: [
      ...steps(BEATS_OFF, 0.25, 0.65, 0.05),
      ...steps(BEATS_ON, 0.25, 0.35, 0.5),
      ...steps(SIXTEENTH_OFFS, 0.25, 0.3, 0.75),
    ],
  },
  phonk: {
    kick: [
      { at: 0, dur: 1, accent: 1, minIntensity: 0 },
      { at: 1.5, dur: 0.5, accent: 0.8, minIntensity: 0.3 },
      { at: 2.75, dur: 0.5, accent: 0.7, minIntensity: 0.6 },
    ],
    snare: [
      { at: 2, dur: 1, accent: 0.95, minIntensity: 0.1 },
      { at: 3.5, dur: 0.5, accent: 0.4, minIntensity: 0.7 },
    ],
    hat: TRIPLET_EIGHTHS.map((at, i) => ({
      at,
      dur: 1 / 3,
      accent: i % 3 === 0 ? 0.75 : 0.45,
      minIntensity: i % 3 === 0 ? 0.02 : 0.3,
    })),
  },
  funk: {
    kick: [
      { at: 0, dur: 0.5, accent: 1, minIntensity: 0 },
      { at: 1.5, dur: 0.5, accent: 0.85, minIntensity: 0.05 },
      { at: 2.5, dur: 0.5, accent: 0.8, minIntensity: 0.25 },
      { at: 3.25, dur: 0.25, accent: 0.6, minIntensity: 0.6 },
    ],
    snare: [
      { at: 0.75, dur: 0.25, accent: 0.7, minIntensity: 0.3 },
      { at: 1, dur: 0.5, accent: 0.9, minIntensity: 0.1 },
      { at: 2.25, dur: 0.25, accent: 0.6, minIntensity: 0.5 },
      { at: 3, dur: 0.5, accent: 0.95, minIntensity: 0.1 },
    ],
    hat: [
      ...steps(BEATS_ON, 0.25, 0.75, 0.02),
      ...steps(BEATS_OFF, 0.25, 0.5, 0.05),
      ...steps(SIXTEENTH_OFFS, 0.25, 0.4, 0.35),
    ],
  },
};

interface ChordStep {
  readonly at: Beat;
  readonly dur: Beat;
  readonly accent: number;
}

const CHORD_RHYTHMS: Record<SectionName, readonly ChordStep[]> = {
  intro: [{ at: 0, dur: 4, accent: 0.6 }],
  groove: [
    { at: 0, dur: 1.5, accent: 0.8 },
    { at: 2.5, dur: 1, accent: 0.7 },
  ],
  build: [
    { at: 0, dur: 0.5, accent: 0.7 },
    { at: 1, dur: 0.5, accent: 0.75 },
    { at: 2, dur: 0.5, accent: 0.8 },
    { at: 3, dur: 0.5, accent: 0.85 },
  ],
  peak: [
    { at: 0, dur: 2, accent: 0.95 },
    { at: 2, dur: 1.5, accent: 0.85 },
    { at: 3.5, dur: 0.5, accent: 0.7 },
  ],
  break: [{ at: 0, dur: 4, accent: 0.7 }],
  outro: [{ at: 0, dur: 4, accent: 0.6 }],
};

interface BassStep {
  readonly at: Beat;
  readonly dur: Beat;
  readonly accent: number;
  readonly step: number;
}

type BassStyle = 'pad' | 'root' | 'drive' | 'slide' | 'sync' | 'sparse';

const BASS_STYLES: Record<BassStyle, readonly BassStep[]> = {
  pad: [{ at: 0, dur: 4, accent: 0.65, step: 0 }],
  root: [
    { at: 0, dur: 1, accent: 0.95, step: 0 },
    { at: 1.5, dur: 0.5, accent: 0.6, step: 0 },
    { at: 2, dur: 1, accent: 0.85, step: 0 },
    { at: 3, dur: 1, accent: 0.7, step: 2 },
  ],
  drive: [
    { at: 0, dur: 0.5, accent: 1, step: 0 },
    { at: 0.5, dur: 0.5, accent: 0.6, step: 0 },
    { at: 1, dur: 0.5, accent: 0.8, step: 0 },
    { at: 1.5, dur: 0.5, accent: 0.6, step: 4 },
    { at: 2, dur: 0.5, accent: 0.95, step: 0 },
    { at: 2.5, dur: 0.5, accent: 0.6, step: 0 },
    { at: 3, dur: 0.5, accent: 0.8, step: 2 },
    { at: 3.5, dur: 0.5, accent: 0.6, step: 4 },
  ],
  slide: [
    { at: 0, dur: 1.75, accent: 1, step: 0 },
    { at: 1.75, dur: 0.75, accent: 0.8, step: 3 },
    { at: 2.5, dur: 1, accent: 0.9, step: -3 },
    { at: 3.5, dur: 0.5, accent: 0.7, step: 0 },
  ],
  sync: [
    { at: 0, dur: 0.75, accent: 0.95, step: 0 },
    { at: 0.75, dur: 0.75, accent: 0.7, step: 4 },
    { at: 1.5, dur: 0.5, accent: 0.85, step: 0 },
    { at: 2.5, dur: 0.5, accent: 0.75, step: 2 },
    { at: 3, dur: 1, accent: 0.7, step: -3 },
  ],
  sparse: [
    { at: 0, dur: 2, accent: 0.7, step: 0 },
    { at: 2, dur: 2, accent: 0.6, step: 4 },
  ],
};

const KIT_BASS_STYLE: Record<KitId, Record<SectionName, BassStyle>> = {
  edm: { intro: 'pad', groove: 'root', build: 'root', peak: 'drive', break: 'sparse', outro: 'sparse' },
  phonk: { intro: 'pad', groove: 'slide', build: 'slide', peak: 'slide', break: 'sparse', outro: 'sparse' },
  funk: { intro: 'pad', groove: 'sync', build: 'sync', peak: 'sync', break: 'sparse', outro: 'sparse' },
};

const LEAD_PATTERNS: readonly (readonly number[])[] = [
  [0, 2, 4, 2, 6, 4, 2, 0],
  [0, 4, 2, 6, 4, 2, 4, 0],
  [4, 2, 0, 2, 4, 6, 4, 2],
  [0, 2, 4, 6, 4, 6, 2, 4],
];

const LEAD_EIGHTHS: readonly number[] = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5];
const FILL_EIGHTHS: readonly number[] = [2, 2.5, 3, 3.25, 3.5, 3.75];

const COUNT_IN_CLICKS: readonly (readonly number[])[] = [
  [0, 2],
  [0, 1, 2, 3],
];
const COUNT_IN_ACCENT = 0.7;
const ROLL_SUBDIVISIONS: readonly number[] = [2, 4];
const ROLL_ACCENT_START = 0.3;
const ROLL_ACCENT_RISE = 0.65;
const MARK_LANE: Lane = 'kick';
const MARK_BEATS: Beat = 0.5;
const MARK_FLOOR = 0.5;
const MARK_EPSILON = 1e-9;

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function scaleSemitone(step: number): number {
  const index = ((step % MINOR_SCALE.length) + MINOR_SCALE.length) % MINOR_SCALE.length;
  const octave = Math.floor(step / MINOR_SCALE.length);
  return (MINOR_SCALE[index] ?? 0) + octave * SEMITONES_PER_OCTAVE;
}

function transpose(rootHz: number, semitones: number): number {
  return rootHz * Math.pow(2, semitones / SEMITONES_PER_OCTAVE);
}

function pitchAt(rootHz: number, step: number, octave: number): number {
  return transpose(rootHz, scaleSemitone(step) + octave * SEMITONES_PER_OCTAVE);
}

function levelFor(lane: Lane, accent: number, intensity: number): number {
  const shaped = INTENSITY_FLOOR + (1 - INTENSITY_FLOOR) * clamp01(intensity);
  return clamp01(LANE_GAIN_BUDGET[lane] * Math.min(accent, 1) * shaped);
}

function laneRank(lane: Lane): number {
  const index = LANE_ORDER.indexOf(lane);
  return index < 0 ? LANE_ORDER.length : index;
}

function compareEvents(a: MusicEvent, b: MusicEvent): number {
  return a.beat === b.beat ? laneRank(a.lane) - laneRank(b.lane) : a.beat - b.beat;
}

function chordVoices(rootHz: number, degree: number, intensity: number): number[] {
  const count = intensity >= CHORD_SEVENTH_INTENSITY ? CHORD_VOICE_LIMIT : CHORD_VOICE_LIMIT - 1;
  const voices: number[] = [];
  for (let i = 0; i < count; i++) voices.push(pitchAt(rootHz, degree + i * 2, CHORD_OCTAVE));
  return voices;
}

export interface Arrangement {
  readonly seed: string;
  readonly kit: KitId;
  readonly form: readonly FormPart[];
  readonly endBar: number;
  sectionAt(bar: number): Section;
  eventsInBars(fromBar: number, toBar: number): readonly MusicEvent[];
  intensityAt(beat: Beat): number;
  harmonyAt(beat: Beat): Harmony;
}

export function createArrangement(
  seed: string,
  kit: KitId,
  form: readonly FormPart[] = SONG_FORM,
  marks: readonly Beat[] = [],
): Arrangement {
  const rootHz = KIT_ROOT_HZ[kit];
  const drums = DRUM_PATTERNS[kit];
  const bassStyles = KIT_BASS_STYLE[kit];
  const endBar = form.reduce((end, part) => Math.max(end, part.bar + part.bars), 0);
  const timeline: Section[] = form.map((part, index) => ({
    bar: part.bar,
    bars: part.bars,
    name: part.name,
    intensity: SECTION_BASE_INTENSITY[part.name],
    rootHz,
    degrees: createRng(`${seed}:${kit}:section:${String(index)}`).pick(PROGRESSIONS),
  }));
  const fallback: Section = {
    bar: 0,
    bars: Math.max(1, endBar),
    name: 'intro',
    intensity: SECTION_BASE_INTENSITY.intro,
    rootHz,
    degrees: PROGRESSIONS[0] ?? [0],
  };

  function sectionAt(bar: number): Section {
    const target = Math.max(0, Math.floor(bar));
    for (let i = timeline.length - 1; i >= 0; i--) {
      const entry = timeline[i];
      if (entry !== undefined && entry.bar <= target) return entry;
    }
    return timeline[0] ?? fallback;
  }

  function intensityAt(beat: Beat): number {
    const bar = Math.max(0, beat / BEATS_PER_BAR);
    const section = sectionAt(bar);
    const progress = clamp01((bar - section.bar) / section.bars);
    const target =
      section.name === 'build' ? SECTION_BASE_INTENSITY.peak : section.intensity;
    return clamp01(section.intensity + (target - section.intensity) * progress);
  }

  function degreeFor(section: Section, barInSection: number): number {
    const size = section.degrees.length;
    if (size === 0) return 0;
    return section.degrees[((barInSection % size) + size) % size] ?? 0;
  }

  function harmonyAt(beat: Beat): Harmony {
    const bar = Math.max(0, Math.floor(beat / BEATS_PER_BAR));
    const section = sectionAt(bar);
    const barInSection = bar - section.bar;
    const degree = degreeFor(section, barInSection);
    return {
      rootHz: section.rootHz,
      degree,
      pitchHz: pitchAt(section.rootHz, degree, 0),
      chordHz: chordVoices(section.rootHz, degree, section.intensity),
      section: section.name,
    };
  }

  function barEvents(bar: number): MusicEvent[] {
    if (bar < 0 || bar >= endBar) return [];
    const section = sectionAt(bar);
    const barInSection = bar - section.bar;
    const rng = createRng(`${seed}:${kit}:bar:${String(bar)}`);
    const origin = bar * BEATS_PER_BAR;
    const lanes = SECTION_LANES[section.name];
    const intensity = section.intensity;
    const density = intensity * SECTION_DENSITY[section.name];
    const lift = section.name === 'build' ? (barInSection + 1) / section.bars : 0;
    const lastBar = barInSection === section.bars - 1;
    const degree = degreeFor(section, barInSection);
    const events: MusicEvent[] = [];

    const drumLanes: readonly DrumLane[] = ['kick', 'snare', 'hat'];
    for (const lane of drumLanes) {
      if (!lanes.includes(lane)) continue;
      for (const step of drums[lane]) {
        const reach = lane === 'hat' ? density + lift * 0.4 : density;
        if (reach < step.minIntensity) continue;
        const swing = lane === 'hat' && rng.chance(0.12 * intensity) ? 0 : 1;
        events.push({
          beat: origin + step.at,
          lane,
          durationBeats: step.dur,
          level: levelFor(lane, step.accent * (lane === 'hat' ? 0.7 + 0.3 * swing : 1), intensity),
        });
      }
    }

    if (lastBar && lanes.includes('snare')) {
      for (const at of FILL_EIGHTHS) {
        if (!rng.chance(0.45 + 0.4 * intensity)) continue;
        events.push({
          beat: origin + at,
          lane: 'snare',
          durationBeats: 0.25,
          level: levelFor('snare', 0.4 + 0.5 * (at / BEATS_PER_BAR), intensity),
        });
      }
    }

    if (lanes.includes('bass')) {
      const style = section.name === 'build' && lift > 0.6 ? 'drive' : bassStyles[section.name];
      for (const step of BASS_STYLES[style]) {
        const octave = BASS_OCTAVE + (rng.chance(0.1 * intensity) ? 1 : 0);
        events.push({
          beat: origin + step.at,
          lane: 'bass',
          durationBeats: step.dur,
          level: levelFor('bass', step.accent, intensity),
          pitchHz: pitchAt(section.rootHz, degree + step.step, octave),
        });
      }
    }

    if (lanes.includes('chord')) {
      const voices = chordVoices(section.rootHz, degree, intensity);
      for (const step of CHORD_RHYTHMS[section.name]) {
        events.push({
          beat: origin + step.at,
          lane: 'chord',
          durationBeats: step.dur,
          level: levelFor('chord', step.accent * (1 - 0.2 * lift), intensity),
          chordHz: voices,
        });
      }
    }

    if (lanes.includes('lead')) {
      const pattern = rng.pick(LEAD_PATTERNS);
      const octave = LEAD_OCTAVE + (section.name === 'peak' ? 1 : 0);
      for (let i = 0; i < LEAD_EIGHTHS.length; i++) {
        const at = LEAD_EIGHTHS[i] ?? 0;
        const tone = pattern[i % pattern.length] ?? 0;
        if (density + lift < 0.35 && i % 2 === 1) continue;
        if (!rng.chance(0.55 + 0.45 * (intensity + lift) * 0.5)) continue;
        events.push({
          beat: origin + at,
          lane: 'lead',
          durationBeats: 0.5,
          level: levelFor('lead', 0.6 + 0.4 * (intensity + lift) * 0.5, intensity),
          pitchHz: pitchAt(section.rootHz, degree + tone, octave),
        });
      }
    }

    const clicks = bar < COUNT_IN_BARS ? COUNT_IN_CLICKS[bar] : undefined;
    if (clicks !== undefined) {
      for (const at of clicks) {
        events.push({
          beat: origin + at,
          lane: 'snare',
          durationBeats: 0.25,
          level: levelFor('snare', COUNT_IN_ACCENT, 1),
        });
      }
    }

    const rollStage = barInSection - (section.bars - ROLL_SUBDIVISIONS.length);
    if (section.name === 'build' && rollStage >= 0) {
      const perBeat = ROLL_SUBDIVISIONS[rollStage] ?? 4;
      const span = ROLL_SUBDIVISIONS.length * BEATS_PER_BAR;
      for (let i = 0; i < BEATS_PER_BAR * perBeat; i++) {
        const at = i / perBeat;
        const ramp = (rollStage * BEATS_PER_BAR + at) / span;
        events.push({
          beat: origin + at,
          lane: 'snare',
          durationBeats: 1 / perBeat,
          level: levelFor('snare', ROLL_ACCENT_START + ROLL_ACCENT_RISE * ramp, intensity),
        });
      }
    }

    for (const mark of marks) {
      if (mark < origin || mark >= origin + BEATS_PER_BAR) continue;
      if (events.some((event) => event.lane === MARK_LANE && Math.abs(event.beat - mark) < MARK_EPSILON)) continue;
      events.push({
        beat: mark,
        lane: MARK_LANE,
        durationBeats: MARK_BEATS,
        level: levelFor(MARK_LANE, 1, Math.max(intensity, MARK_FLOOR)),
      });
    }

    return events.sort(compareEvents);
  }

  function eventsInBars(fromBar: number, toBar: number): readonly MusicEvent[] {
    const start = Math.max(0, Math.floor(fromBar));
    const end = Math.floor(toBar);
    const out: MusicEvent[] = [];
    for (let bar = start; bar < end; bar++) out.push(...barEvents(bar));
    return out;
  }

  return {
    seed,
    kit,
    form,
    endBar,
    sectionAt,
    eventsInBars,
    intensityAt,
    harmonyAt,
  };
}
